import React, { useRef, useState, useEffect } from "react";
import emailjs from "@emailjs/browser";
import "../styles/Contact.css";

function Contact() {
  const form = useRef();
  const [values, setValues] = useState({
    name: "",
    email: "",
    _subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value || value.trim() === "") return "Nama harus diisi.";
        if (value.trim().length < 2) return "Nama terlalu pendek.";
        return "";
      case "email":
        if (!value || value.trim() === "") return "Email harus diisi.";
        if (!emailRegex.test(value)) return "Format email tidak valid.";
        return "";
      case "_subject":
        if (value && value.length > 350)
          return "Subject maksimal 350 karakter.";
        return "";
      case "message":
        if (!value || value.trim() === "") return "Pesan harus diisi.";
        if (value.trim().length < 25)
          return "Pesan terlalu singkat (min 25 karakter).";
        return "";
      default:
        return "";
    }
  };

  const validateAll = () => {
    const next = {};
    Object.keys(values).forEach((k) => {
      const err = validateField(k, values[k]);
      if (err) next[k] = err;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((s) => ({ ...s, [name]: value }));
    // live-validate this field
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    // clear any global status when user edits
    if (status.type) setStatus({ type: "", message: "" });
  };

  const isFormValid = () => {
    // required fields
    const name = values.name || "";
    const email = values.email || "";
    const message = values.message || "";

    if (name.trim() === "" || email.trim() === "" || message.trim() === "")
      return false;
    // quick rules consistent with validateField
    if (name.trim().length < 2) return false;
    if (!emailRegex.test(email)) return false;
    if (message.trim().length < 10) return false;

    // any live validation errors present?
    const hasErrors = Object.values(errors).some((v) => v && v.length > 0);
    if (hasErrors) return false;

    return true;
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    if (!validateAll()) return;

    setIsSending(true);
    try {
      await emailjs.sendForm(
        "service_8xhd33a",
        "template_rrt51c9",
        form.current,
        {
          publicKey: "rfE60QvDDCSXbYM89",
        }
      );
      setStatus({ type: "success", message: "Pesan berhasil terkirim!" });
      form.current.reset();
      setValues({ name: "", email: "", _subject: "", message: "" });
      setErrors({});
    } catch (err) {
      console.error(err);
      setStatus({
        type: "error",
        message: "Gagal mengirim pesan. Silakan coba lagi.",
      });
    } finally {
      setIsSending(false);
    }
  };

  // auto-dismiss success message after 5 seconds
  useEffect(() => {
    if (status.type !== "success") return;
    const id = setTimeout(() => {
      setStatus({ type: "", message: "" });
    }, 5000);
    return () => clearTimeout(id);
  }, [status.type]);

  return (
    <div className="contact" id="contact">
      <h1>Contact</h1>

      <form ref={form} onSubmit={sendEmail} noValidate>
        {status.message && (
          <div
            className={`form-status ${
              status.type === "error" ? "error" : "success"
            }`}
            role="status"
            aria-live="polite"
          >
            {status.message}
          </div>
        )}
        <div className="field">
          <input
            type="text"
            name="name"
            placeholder="Name"
            value={values.name}
            onChange={handleChange}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? "err-name" : undefined}
          />
          {errors.name && (
            <small id="err-name" className="error" role="alert">
              {errors.name}
            </small>
          )}
        </div>

        <div className="field">
          <input
            type="email"
            name="email"
            placeholder="E-Mail"
            value={values.email}
            onChange={handleChange}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "err-email" : undefined}
          />
          {errors.email && (
            <small id="err-email" className="error" role="alert">
              {errors.email}
            </small>
          )}
        </div>

        <div className="field">
          <input
            type="text"
            name="_subject"
            placeholder="Subject"
            value={values._subject}
            onChange={handleChange}
            aria-invalid={!!errors._subject}
            aria-describedby={errors._subject ? "err-subject" : undefined}
          />
          {errors._subject && (
            <small id="err-subject" className="error" role="alert">
              {errors._subject}
            </small>
          )}
        </div>

        <div className="field">
          <textarea
            name="message"
            placeholder="Message"
            value={values.message}
            onChange={handleChange}
            aria-invalid={!!errors.message}
            aria-describedby={errors.message ? "err-message" : undefined}
          />
          {errors.message && (
            <small id="err-message" className="error" role="alert">
              {errors.message}
            </small>
          )}
        </div>

        <button
          type="submit"
          disabled={isSending || !isFormValid()}
          className={isSending || !isFormValid() ? "disabled" : ""}
        >
          {isSending ? "Sending..." : "Send"}
        </button>
      </form>
    </div>
  );
}

export default Contact;
